import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import CampoArea from '@/components/base/CampoArea';
import Botao from '@/components/ui/Botao';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { enviarComprovante } from '@/lib/comprovante';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import { dataISO, FORMAS_LANCAMENTO, hojeISO, num } from './constantes';
import type { LinhaExtrato } from './types';

interface Props {
  tipo: 'entrada' | 'saida';
  editar: LinhaExtrato | null;
  categorias: string[];
  eventoId: number;
  aoFechar: () => void;
}

const SELECT_CLS =
  'mt-1 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

export default function ModalLancamento({
  tipo,
  editar,
  categorias,
  eventoId,
  aoFechar,
}: Props) {
  const ehEntrada = tipo === 'entrada';
  const padrao = ehEntrada ? 'Doação' : 'Outros';

  const opcoes = useMemo(() => {
    const set = new Set<string>();
    categorias.forEach((c) => {
      if (c) set.add(c);
    });
    if (editar?.categoria) set.add(editar.categoria);
    set.add(padrao);
    return Array.from(set);
  }, [categorias, editar, padrao]);

  const [descricao, setDescricao] = useState(editar?.descricao ?? '');
  const [valorCentavos, setValorCentavos] = useState(() =>
    editar ? Math.round(num(editar.valor) * 100) : 0,
  );
  const [categoria, setCategoria] = useState(editar?.categoria ?? padrao);
  const [quem, setQuem] = useState(editar?.lancado_por ?? '');
  const [data, setData] = useState(() => (editar ? dataISO(editar.quando) : hojeISO()));
  const [forma, setForma] = useState(editar?.forma ?? 'dinheiro');
  const [observacao, setObservacao] = useState(editar?.observacao ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvoSemComprovante, setSalvoSemComprovante] = useState(false);
  const comprovanteRef = useRef<HTMLInputElement>(null);

  // Garante que a categoria continue válida mesmo se as opções mudarem.
  useEffect(() => {
    if (!opcoes.includes(categoria)) {
      setCategoria(opcoes.includes(padrao) ? padrao : opcoes[0] ?? padrao);
    }
  }, [opcoes, categoria, padrao]);

  const titulo = editar
    ? ehEntrada
      ? 'Editar entrada'
      : 'Editar saída'
    : ehEntrada
      ? 'Lançar entrada'
      : 'Lançar saída';

  async function salvar() {
    if (!descricao.trim()) {
      setErro('Informe uma descrição.');
      return;
    }
    if (valorCentavos <= 0) {
      setErro('Informe um valor maior que zero.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const base = {
        p_evento: eventoId,
        p_descricao: descricao.trim(),
        p_valor_reais: valorCentavos / 100,
        p_categoria: categoria,
        p_forma: forma || null,
        p_observacao: observacao.trim() || null,
        p_id: editar ? Number(editar.lancamento_id) : null,
      };

      let despesaId: number | null =
        editar && !ehEntrada ? Number(editar.lancamento_id) : null;

      if (ehEntrada) {
        const { error } = await supabase.rpc('salvar_entrada', {
          ...base,
          p_recebido_em: data,
          p_recebido_de: quem.trim() || null,
        });
        if (error) throw error;
      } else {
        const { data: idSalvo, error } = await supabase.rpc('salvar_despesa', {
          ...base,
          p_pago_em: data,
          p_pago_por: quem.trim() || null,
        });
        if (error) throw error;
        if (!editar) despesaId = Number(idSalvo);
      }

      // Só depois de a despesa existir (com id) é que o arquivo sobe.
      if (!ehEntrada && arquivo && despesaId) {
        try {
          await enviarComprovante({
            eventoId,
            despesaId,
            arquivo,
            pathAntigo: editar?.comprovante_path ?? null,
          });
        } catch (err) {
          setErro(
            mensagemAmigavel(
              err,
              'Não foi possível enviar o comprovante. A despesa ficou salva; tente anexar de novo.',
            ),
          );
          setSalvoSemComprovante(true);
          return;
        }
      }

      aoFechar();
    } catch (e) {
      setErro(mensagemBanco(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
      <div className="animate-modal-pop max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-lg bg-white p-6 sm:rounded-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-medium text-foreground-950">{titulo}</h3>
            <p className="mt-1 text-sm text-secondary-500">
              {ehEntrada
                ? 'Doação, dinheiro recebido na hora ou outra entrada.'
                : 'Uma despesa do evento.'}
            </p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            disabled={salvando}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <CampoTexto
            id="lancamento-descricao"
            name="descricao"
            label="Descrição"
            value={descricao}
            onChange={setDescricao}
            required
            placeholder={ehEntrada ? 'Ex.: Doação da família Souza' : 'Ex.: Som e luz'}
            autoFocus
          />

          <CampoDinheiro
            id="lancamento-valor"
            name="valor"
            label="Valor"
            centavos={valorCentavos}
            onChange={setValorCentavos}
            required
          />

          <div>
            <label htmlFor="lancamento-categoria" className="block text-base font-medium text-foreground-950">
              Categoria
            </label>
            <select
              id="lancamento-categoria"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className={SELECT_CLS}
            >
              {opcoes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <CampoTexto
            id="lancamento-quem"
            name="quem"
            label={ehEntrada ? 'Recebido de' : 'Pago para'}
            value={quem}
            onChange={setQuem}
            placeholder={ehEntrada ? 'Ex.: Família Souza' : 'Ex.: Fornecedor de som'}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="lancamento-data" className="block text-base font-medium text-foreground-950">
                {ehEntrada ? 'Recebido em' : 'Pago em'}
              </label>
              <input
                id="lancamento-data"
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className={SELECT_CLS}
              />
            </div>
            <div>
              <label htmlFor="lancamento-forma" className="block text-base font-medium text-foreground-950">
                Forma
              </label>
              <select
                id="lancamento-forma"
                value={forma}
                onChange={(e) => setForma(e.target.value)}
                className={SELECT_CLS}
              >
                {FORMAS_LANCAMENTO.map((f) => (
                  <option key={f.valor} value={f.valor}>
                    {f.rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <CampoArea
            id="lancamento-observacao"
            name="observacao"
            label="Observação"
            value={observacao}
            onChange={setObservacao}
            placeholder="Opcional"
            maxLength={500}
            simples
          />

          {!ehEntrada && (
            <div>
              <span className="block text-base font-medium text-foreground-950">
                Comprovante (foto ou PDF)
              </span>
              <p className="mt-1 text-sm text-secondary-500">
                Opcional. Ajuda a comprovar a despesa depois.
              </p>
              <input
                ref={comprovanteRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Botao
                  variante="secundario"
                  tamanho="md"
                  icone="ri-attachment-2"
                  onClick={() => comprovanteRef.current?.click()}
                  disabled={salvando || salvoSemComprovante}
                >
                  {arquivo ? 'Trocar arquivo' : editar?.comprovante_path ? 'Trocar comprovante' : 'Escolher arquivo'}
                </Botao>
                {arquivo ? (
                  <span className="flex min-w-0 items-center gap-1.5 text-xs text-secondary-600">
                    <i className="ri-file-line shrink-0" />
                    <span className="truncate">{arquivo.name}</span>
                  </span>
                ) : editar?.comprovante_path ? (
                  <span className="flex items-center gap-1.5 text-xs text-secondary-500">
                    <i className="ri-attachment-2 shrink-0" /> Comprovante já anexado
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {erro && (
          <p className="mt-4 text-sm text-danger-600" role="alert">
            {erro}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          {salvoSemComprovante ? (
            <button
              type="button"
              onClick={aoFechar}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Fechar
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={aoFechar}
                disabled={salvando}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvar}
                disabled={salvando}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}